// Showcase Mode - Museum-quality automotive presentation
// "Museum, Not Screensaver" - deliberate, prestigious, zero chrome

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useDrag } from '@use-gesture/react';
import type { ImageMetadata, Vehicle } from '@/types';

interface ShowcaseModeProps {
  vehicles: Map<string, Vehicle>;
  heroImages: ImageMetadata[];
  onExit: () => void;
  onImageSelect?: (image: ImageMetadata) => void; // Click to view image
  rotationIntervalMs?: number; // Default 10 seconds per image
}

export function ShowcaseMode({
  vehicles,
  heroImages,
  onExit,
  onImageSelect,
  rotationIntervalMs = 10000,
}: ShowcaseModeProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Current image and its vehicle
  const currentImage = heroImages[currentIndex];
  const vehicle = currentImage ? vehicles.get(currentImage.vehicleId) : undefined;

  // Auto-advance to next image
  useEffect(() => {
    if (heroImages.length <= 1) return;

    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, rotationIntervalMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentIndex, heroImages.length, rotationIntervalMs]);

  // Exit handler
  const handleExit = useCallback(() => {
    setIsExiting(true);
    setTimeout(onExit, 300);
  }, [onExit]);

  // Click/tap to select current image (or exit if no handler)
  const handleClick = useCallback(() => {
    if (onImageSelect && currentImage) {
      setIsExiting(true);
      setTimeout(() => onImageSelect(currentImage), 300);
    } else {
      handleExit();
    }
  }, [onImageSelect, currentImage, handleExit]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % heroImages.length);
  }, [heroImages.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, [heroImages.length]);

  // Swipe navigation + tap detection via useDrag
  const bind = useDrag(
    ({ movement: [mx], velocity: [vx], direction: [dx], tap, cancel }) => {
      if (tap) {
        handleClick();
        return;
      }
      if (Math.abs(vx) > 0.3 || Math.abs(mx) > 80) {
        if (dx > 0) goToPrevious();
        else goToNext();
        cancel();
      }
    },
    { filterTaps: true }
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleExit();
      } else if (e.key === ' ' || e.key === 'Enter') {
        handleClick();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleExit, handleClick, goToNext, goToPrevious]);

  if (!currentImage) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 bg-black cursor-pointer"
    >
    <div
      {...bind()}
      className="absolute inset-0"
      style={{ touchAction: 'none' }}
    >
      {/* Image with Ken Burns effect */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImage.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <KenBurnsImage
            src={currentImage.url}
            alt={currentImage.componentDetail}
            duration={rotationIntervalMs}
            index={currentIndex}
          />
        </motion.div>
      </AnimatePresence>

      {/* Cinematic vignette overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      </div>

      {/* Sparse overlay - vehicle info (bottom left), updates per image */}
      <div className="absolute bottom-8 left-8 pointer-events-none">
        <AnimatePresence mode="wait">
          {vehicle && (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-white/60 text-lg tracking-widest uppercase font-light mb-1">
                {vehicle.make}
              </p>
              <h1 className="text-white text-5xl font-light tracking-wide">
                {vehicle.year} {vehicle.model}
              </h1>
              {vehicle.name && vehicle.name !== `${vehicle.year} ${vehicle.model}` && (
                <p className="text-white/50 text-xl mt-2 italic">
                  "{vehicle.name}"
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Image info overlay (bottom right) */}
      <div className="absolute bottom-8 right-8 pointer-events-none text-right">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImage.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-white/70 text-lg mb-1">
              {currentImage.componentDetail}
            </p>
            <span className="text-white/40 text-sm tracking-wider uppercase">
              {currentImage.condition.replace('-', ' ')}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress indicators */}
      {heroImages.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 pointer-events-none">
          {heroImages.map((_, index) => (
            <motion.div
              key={index}
              className="relative"
              initial={false}
            >
              <div
                className={clsx(
                  'w-2 h-2 rounded-full transition-all duration-500',
                  index === currentIndex
                    ? 'bg-white/80'
                    : 'bg-white/20'
                )}
              />
              {/* Progress fill animation for current */}
              {index === currentIndex && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-white/40"
                  initial={{ scale: 1 }}
                  animate={{ scale: 2.5, opacity: 0 }}
                  transition={{ duration: rotationIntervalMs / 1000, ease: 'linear' }}
                />
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Subtle hint */}
      <ExitHint />
    </div>
    </motion.div>
  );
}

/**
 * Ken Burns effect image component - more dynamic
 */
function KenBurnsImage({
  src,
  alt,
  duration,
  index,
}: {
  src: string;
  alt: string;
  duration: number;
  index: number;
}) {
  // More varied Ken Burns movements
  const transforms = [
    // Slow zoom in from center
    { startScale: 1, endScale: 1.15, startX: 0, endX: 0, startY: 0, endY: 0 },
    // Zoom out while panning right
    { startScale: 1.2, endScale: 1, startX: -5, endX: 3, startY: 0, endY: 0 },
    // Pan left to right with slight zoom
    { startScale: 1.05, endScale: 1.1, startX: 4, endX: -4, startY: 0, endY: 0 },
    // Diagonal pan with zoom in
    { startScale: 1, endScale: 1.12, startX: 3, endX: -3, startY: 2, endY: -2 },
    // Zoom out from corner
    { startScale: 1.18, endScale: 1, startX: -4, endX: 2, startY: -3, endY: 1 },
    // Slow rise up with zoom
    { startScale: 1.02, endScale: 1.1, startX: 0, endX: 0, startY: 3, endY: -2 },
    // Pan down with slight zoom out
    { startScale: 1.12, endScale: 1.05, startX: 2, endX: -1, startY: -4, endY: 2 },
    // Dramatic zoom in on center-right
    { startScale: 1, endScale: 1.2, startX: -3, endX: -5, startY: 0, endY: 0 },
  ];

  // Use index to get consistent but varied transform per image
  const transform = transforms[index % transforms.length];

  return (
    <motion.img
      src={src}
      alt={alt}
      className="absolute inset-0 w-full h-full object-cover"
      initial={{
        scale: transform.startScale,
        x: `${transform.startX}%`,
        y: `${transform.startY}%`,
      }}
      animate={{
        scale: transform.endScale,
        x: `${transform.endX}%`,
        y: `${transform.endY}%`,
      }}
      transition={{
        duration: duration / 1000,
        ease: [0.25, 0.1, 0.25, 1], // Smooth easing
      }}
    />
  );
}

/**
 * Subtle exit hint
 */
function ExitHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 3000);
    const hideTimer = setTimeout(() => setVisible(false), 7000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5 }}
          className="absolute top-8 left-1/2 -translate-x-1/2 pointer-events-none"
        >
          <p className="text-white/40 text-sm tracking-widest uppercase">
            Tap to view • Say "Hey Ventura" to search
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
